import { apiSlice } from './apiSlice';

export interface SimpleRolePolicyStatement {
  actions?: string[];
  resources?: string[];
  effect?: string;
}

export interface SimpleRolePolicySpec {
  name: string;
  statements: SimpleRolePolicyStatement[];
  ttlSeconds: number;
}

export interface SimpleRole {
  _id: string;
  name: string;
  description?: string;
  orgId?: string;
  projectId?: string;
  ownerUserId: string;
  policySpecs: SimpleRolePolicySpec[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface DataResponse<T> {
  data: T;
}

export const simpleRolesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSimpleRoles: builder.query<SimpleRole[], { orgId: string }>({
      query: ({ orgId }) => ({ url: '/roles', params: { orgId } }),
      transformResponse: (response: DataResponse<SimpleRole[]>) => response.data,
      providesTags: (result, _error, { orgId }) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'SimpleRole' as const, id: _id })),
              { type: 'SimpleRole', id: `ORG-${orgId}` },
            ]
          : [{ type: 'SimpleRole', id: `ORG-${orgId}` }],
    }),
    getSimpleRole: builder.query<SimpleRole, string>({
      query: (id) => `/roles/${id}`,
      transformResponse: (response: DataResponse<SimpleRole>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'SimpleRole', id }],
    }),
    createSimpleRole: builder.mutation<
      { roleId: string; capabilities: string[] },
      { orgId: string; name: string; capabilities: string[] }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/organizations/${orgId}/roles/simple`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: DataResponse<{ roleId: string; capabilities: string[] }>) =>
        response.data,
      invalidatesTags: (_result, _error, { orgId }) => [{ type: 'SimpleRole', id: `ORG-${orgId}` }],
    }),
  }),
});

export const { useGetSimpleRolesQuery, useGetSimpleRoleQuery, useCreateSimpleRoleMutation } =
  simpleRolesApi;
